/**
 * features/admin/CommentTemplatesManager.tsx
 * ------------------------------------------------------------------
 * AUDIT M2. Admin screen to manage the approved comment-template
 * bank that the template/AI suggestion feature reads. Faithful to
 * Al-Nur's CommentTemplatesManager data model:
 *   commentTemplates / subjectTemplatesByForm
 *                    / subjectTemplatesGeneral
 *                    / classTeacherTemplates
 * Templates use {name}. With zero seeded data the suggestion
 * feature still works via built-in fallbacks (commentTemplates.ts).
 * ------------------------------------------------------------------
 */
import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
// Batch B: undefined-safe write + save modal
import { setDoc } from '@/lib/firebase';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { Plus, Trash2, Save as SaveIcon, Check, Sparkles } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { Spinner } from '@/components/ui/primitives';
import {
  GradientHero,
  GlassPill,
  SoftCard,
  glassFieldClass,
} from '@/components/ui/glass';
import { FileText } from 'lucide-react';
import {
  MARK_CATEGORIES,
  CATEGORY_LABEL,
  BUILT_IN_FALLBACKS,
  type MarkCategory,
  type SubjectTemplatesGeneral,
} from '@/lib/commentTemplates';

const GENERAL_DOC = 'subjectTemplatesGeneral';

export default function CommentTemplatesManager() {
  const { subjects, loading: dataLoading } = useData();

  const [general, setGeneral] = useState<SubjectTemplatesGeneral>({});
  const [subjectId, setSubjectId] = useState('');
  const [category, setCategory] = useState<MarkCategory>('good');
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(
    null,
  );
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDoc(
        doc(db, COLLECTIONS.commentTemplates, GENERAL_DOC),
      );
      setGeneral(
        snap.exists() ? (snap.data() as SubjectTemplatesGeneral) : {},
      );
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const current =
    (subjectId && general[subjectId]?.[category]) || [];

  function addTemplate() {
    const t = draft.trim();
    if (!subjectId) {
      setMsg({ ok: false, text: 'Pick a subject first.' });
      return;
    }
    if (!t) return;
    if (!t.includes('{name}')) {
      setMsg({
        ok: false,
        text: 'Template must include the {name} placeholder.',
      });
      return;
    }
    setGeneral((g) => {
      const next = { ...g };
      next[subjectId] = { ...(next[subjectId] ?? {}) };
      const list = [...(next[subjectId][category] ?? []), t];
      next[subjectId][category] = list;
      return next;
    });
    setDraft('');
    setDirty(true);
    setMsg(null);
  }

  function removeTemplate(i: number) {
    setGeneral((g) => {
      const next = { ...g };
      const list = [...(next[subjectId]?.[category] ?? [])];
      list.splice(i, 1);
      next[subjectId] = { ...(next[subjectId] ?? {}) };
      next[subjectId][category] = list;
      return next;
    });
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    setSaveModal({ show: true, status: 'saving', message: 'Saving templates…' });
    try {
      await setDoc(
        doc(db, COLLECTIONS.commentTemplates, GENERAL_DOC),
        general,
      );
      setDirty(false);
      setSaveModal({ show: true, status: 'success', message: 'Templates saved.' });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  if (dataLoading || loading)
    return <Spinner label="Loading templates…" />;

  return (
    <div className="space-y-4">
      <GradientHero
        icon={FileText}
        title="Comment Templates"
        subtitle="Approved phrases the suggestion engine draws from"
        actions={
          <>
            {dirty ? (
              <GlassPill tone="amber">
                <span className="w-2 h-2 bg-amber-300 rounded-full animate-pulse" />
                Unsaved
              </GlassPill>
            ) : (
              <GlassPill tone="green">Saved</GlassPill>
            )}
            <button
              onClick={save}
              disabled={!dirty || saving}
              className={`flex items-center gap-2 px-6 py-3 font-semibold rounded-xl transition-all duration-300 shadow-lg ${
                !dirty
                  ? 'bg-white/20 text-white/60 cursor-not-allowed'
                  : 'bg-white text-[#1b3a6b] hover:bg-blue-50 hover:scale-105'
              }`}
            >
              <SaveIcon size={18} />
              {saving ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      />

      {msg && (
        <div
          className={`text-sm rounded-xl px-4 py-3 border flex items-center gap-2 ${
            msg.ok
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
              : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
          }`}
        >
          {msg.ok ? <Check size={15} /> : null}
          {msg.text}
        </div>
      )}

      <SoftCard pad="p-5">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              Subject
            </label>
            <select
              value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)}
              className={glassFieldClass('mt-1.5')}
            >
              <option value="">— select subject —</option>
              {[...subjects]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              Mark category
            </label>
            <select
              value={category}
              onChange={(e) =>
                setCategory(e.target.value as MarkCategory)
              }
              className={glassFieldClass('mt-1.5')}
            >
              {MARK_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABEL[c]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </SoftCard>

      {subjectId ? (
        <SoftCard pad="p-5">
          <div className="flex items-end gap-2 mb-4">
            <div className="flex-1">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                Add a template{' '}
                <span className="text-xs font-normal text-gray-500">
                  (use {'{name}'} for the student's first name)
                </span>
              </label>
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) =>
                  e.key === 'Enter' && addTemplate()
                }
                placeholder="{name} has shown strong understanding this term."
                className={glassFieldClass('mt-1.5')}
              />
            </div>
            <button
              type="button"
              onClick={addTemplate}
              className="flex items-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-[#2d5295] to-[#1b3a6b] text-white font-semibold text-sm hover:scale-[1.02] transition-all"
            >
              <Plus size={16} />
              Add
            </button>
          </div>

          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            {CATEGORY_LABEL[category]} — {current.length} approved
          </p>
          <div className="space-y-2">
            {current.map((t, i) => (
              <div
                key={i}
                className="flex items-start gap-2 rounded-xl border border-gray-200 dark:border-gray-700 p-3"
              >
                <span className="flex-1 text-sm text-gray-800 dark:text-gray-100">
                  {t}
                </span>
                <button
                  type="button"
                  onClick={() => removeTemplate(i)}
                  className="text-gray-400 hover:text-red-500 transition-colors shrink-0"
                  title="Remove"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            {current.length === 0 && (
              <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 p-4">
                <p className="text-sm text-gray-500 flex items-center gap-2">
                  <Sparkles size={14} className="text-secondary" />
                  No approved templates here yet. The suggestion
                  engine will use these built-in fallbacks until you
                  add your own:
                </p>
                <ul className="mt-2 space-y-1">
                  {BUILT_IN_FALLBACKS[category].map((t, i) => (
                    <li
                      key={i}
                      className="text-xs text-gray-500 italic pl-6"
                    >
                      “{t}”
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </SoftCard>
      ) : (
        <SoftCard pad="p-8">
          <p className="text-sm text-gray-500 text-center">
            Select a subject to manage its approved comment
            templates.
          </p>
        </SoftCard>
      )}
      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
    </div>
  );
}
